import type { Control, Path, UseFormWatch } from "react-hook-form";
import { Controller } from "react-hook-form";

import {
  Icon,
  type IconColor,
  type IconReference,
  Text,
} from "@prophero-ds/cx-react";

import PasswordInput from "./PasswordInput";

type PasswordCopy = {
  passwordLabel: string;
  passwordPlaceholder?: string;
  passwordRequired: string;
  passwordDoesNotMeetRequirements: string;
  confirmPasswordLabel: string;
  confirmPasswordPlaceholder?: string;
  confirmPasswordRequired: string;
  passwordsDoNotMatch: string;
  ruleLength: string;
  ruleMixCase: string;
  ruleNumber: string;
  ruleMatch: string;
};

type PasswordFieldValues = {
  password?: string;
  confirmPassword?: string;
};

type PasswordFieldsProps<T extends PasswordFieldValues> = {
  control: Control<T>;
  watch: UseFormWatch<T>;
  passwordSDKError?: string;
  isPasswordValid: boolean;
  copy: PasswordCopy;
  /** Wraps the fields and rules. Omit to keep them as direct form children. */
  groupClassName?: string;
};

function PasswordFields<T extends PasswordFieldValues>({
  control,
  watch,
  passwordSDKError,
  isPasswordValid,
  copy,
  groupClassName,
}: PasswordFieldsProps<T>) {
  const passwordFieldName = "password" as Path<T>;
  const confirmPasswordFieldName = "confirmPassword" as Path<T>;
  const passwordValue = watch(passwordFieldName) ?? "";
  const confirmPasswordValue = watch(confirmPasswordFieldName) ?? "";

  const passwordsMatch =
    passwordValue &&
    confirmPasswordValue &&
    passwordValue === confirmPasswordValue;
  const matchStatus = !confirmPasswordValue
    ? "neutral"
    : passwordsMatch
      ? "valid"
      : "error";

  const lengthStatus = !passwordValue
    ? "neutral"
    : passwordValue.length >= 8
      ? "valid"
      : "error";
  const numberStatus = !passwordValue
    ? "neutral"
    : /[0-9]/.test(passwordValue)
      ? "valid"
      : "error";
  const mixCaseStatus = !passwordValue
    ? "neutral"
    : /[A-Z]/.test(passwordValue) && /[a-z]/.test(passwordValue)
      ? "valid"
      : "error";

  const renderRule = (status: "valid" | "error" | "neutral", text: string) => {
    const isSuccess = status === "valid";
    const isError = status === "error";
    const icon: IconReference = isSuccess
      ? { name: "check-circle", weight: "fill" }
      : { name: "x-circle", weight: "fill" };
    const iconColor: IconColor = isSuccess ? "success" : "destructive";

    return (
      <div className="flex items-center gap-1">
        {status === "neutral" ? (
          <span
            aria-hidden
            className="inline-block size-3.5 shrink-0 rounded-full border border-[color:var(--cx-fg-secondary)]"
          />
        ) : (
          <Icon {...icon} color={iconColor} size="3.5" aria-hidden />
        )}
        <Text
          as="span"
          variant="body2"
          color={isError ? undefined : "secondary"}
          className={
            isError ? "text-[color:var(--cx-status-critical)]" : undefined
          }
        >
          {text}
        </Text>
      </div>
    );
  };

  const fields = (
    <>
      <Controller
        control={control}
        name={passwordFieldName}
        rules={{
          required: copy.passwordRequired,
          validate: () => {
            if (!isPasswordValid) {
              return copy.passwordDoesNotMeetRequirements;
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => {
          const helperText = fieldState.error?.message ?? passwordSDKError;
          const hasError = !!helperText;

          return (
            <PasswordInput
              label={copy.passwordLabel}
              placeholder={copy.passwordPlaceholder}
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={hasError ? String(helperText) : false}
              autoComplete="new-password"
            />
          );
        }}
      />

      <Controller
        control={control}
        name={confirmPasswordFieldName}
        rules={{
          required: copy.confirmPasswordRequired,
          validate: (value) =>
            value === passwordValue || copy.passwordsDoNotMatch,
        }}
        render={({ field, fieldState }) => {
          const helperText = fieldState.error?.message;
          const hasError = !!helperText;

          return (
            <PasswordInput
              label={copy.confirmPasswordLabel}
              placeholder={copy.confirmPasswordPlaceholder}
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={hasError ? String(helperText) : false}
              autoComplete="new-password"
            />
          );
        }}
      />

      <div className="flex flex-col gap-2">
        {renderRule(lengthStatus, copy.ruleLength)}
        {renderRule(mixCaseStatus, copy.ruleMixCase)}
        {renderRule(numberStatus, copy.ruleNumber)}
        {renderRule(matchStatus, copy.ruleMatch)}
      </div>
    </>
  );

  if (!groupClassName) {
    return fields;
  }

  return <div className={groupClassName}>{fields}</div>;
}

export default PasswordFields;
